import React from 'react'

import * as c from '../../../common'
import {
  TOTAL_PENDING_LABEL,
  totalCostPendingApply,
} from '../../../common/helpers/totalCostDisplay'
import {
  appliedMonthlyPayment,
  isSigningFlowV2,
} from '../../../common/helpers/signingFlowV2'
import { parseCustomerAddress } from '../../../common/helpers/customerAddress'
import { isCashPayment } from '../../../common/helpers/paymentDisplay'
import * as r from '../../../react-utils'

import Button from '@mui/material/Button'

import * as gs from '../../../app/gStyles'
import * as cp from '../../comps'

import ContractLogo, { useContractWatermark } from './ContractLogo'
import { DetailHeader, DetailLine, DetailPlainLine } from './Detail'

import ReportPages, { horizontalPad } from './ReportPages'
//@ts-ignore
import sharedStyles from './SecurityContract.module.css'
//@ts-ignore
import styles from './MSElectricContract.module.css'

// Same dash width as `c.scopeSectionHeader` (47 chars), which cannot be used
// here: its label for `security_system` is the internal name, never shown to
// a customer (T56).
const smartHomeDivider = (() => {
  const label = 'Smart Home'
  const dashes = '-'.repeat(
    Math.max(3, Math.floor((47 - label.length - 2) / 2)),
  )
  return `${dashes} ${label} ${dashes}`
})()

/**
 * Dedicated M+S Electric "Home Improvement Agreement" contract — built
 * page-by-page from the literal reference PDF Fernando Sabbadini sent
 * (Slack, 2026-08-04, "Compliant Agreement (M+S Electric).pdf").
 *
 * Deliberately standalone: does NOT import/extend `FirstElectricContract.tsx`
 * (shared FE/HC/Other "Purchase Agreement") or `HomeImprovementAgreement.tsx`
 * (Caballero's Electric's own component). Luis (2026-08-04), verbatim on why:
 * "Hazle su propio componente a MS Electric, hasta cuando guevonadas con FE y
 * Caballeros, que tenga su propia vaina... si el que va a quedar es el de MS
 * Electric como vamos a depender de un componente obsoleto que va a quedar
 * legacy? Caballeros podría depender de MS Electric, no al reves." MSE is
 * meant to be the canonical/primary contract going forward — CE could
 * eventually be rebuilt on top of this one, not the other way around.
 *
 * Always "HOME IMPROVEMENT" — never the dynamic CCTV/Home Improvement
 * substitution `FirstElectricContract.tsx`/`HomeImprovementAgreement.tsx`
 * still carry (Jaron, Slack: "Quitar CCTV (ya no se vende), puro home
 * improvement para MS Electric").
 *
 * Uses the `home_improvement_*` schema fields (schema.ts, T41) — generic,
 * not CE-owned, originally built for exactly this boxed cover-page layout
 * before T56 stripped that layout out of CE's own component in favor of an
 * auto-generated Scope of Work table. Reusing the fields is fine (pure data
 * shape); reusing CE's rendering code is what Luis ruled out.
 */
/**
 * La empresa que firma el contrato. `address1`/`address2`/`website`/`bidLimit`
 * son opcionales porque no todas las tienen: el registro de Caballero's en
 * `Misc/env/contractCompanies/CE` no guarda direccion — sus campos address1 y
 * address2 llevan el website y el bid limit metidos ahi. Cada linea se pinta
 * solo si existe, en vez de imprimir una direccion falsa o un hueco.
 */
export type ContractPartyInfo = c.r<{
  address1?: string
  address2?: string
  bidLimit?: string
  email: string
  license: string
  name: string
  phone: string
  website?: string
}>

export type MSElectricContractProps = c.r<{
  /**
   * Por defecto M+S Electric. Caballero's (CE) renderiza este mismo contrato
   * desde 2026-09-09: quedo congelado en el estado de MSE del 4 de agosto y
   * Jaron lo pidio igual ("On Caballero Electric, please use the same
   * format"). El fichero ya decia que esa era la direccion correcta — Luis,
   * 2026-08-04: "Caballeros podria depender de MS Electric, no al reves".
   */
  company?: ContractPartyInfo
  customerID: string
  isPDF: boolean
  readOnly?: boolean
  toggleSignOwner1(): void
  toggleSignOwner2(): void
  toggleSignCompany(): void
  toggleCancelSignOwner1(): void
  toggleCancelSignOwner2(): void
  toggleConfirmEquipmentSign(): void
  toggleConfirmTotalInitials(): void
  /** Signing v2: opens the Financing Information signature dialog. */
  toggleFinancingInfoSign?(): void
  /**
   * ESR LLC (Monday 13376550851, Jaron Oct 23): "It can say the same thing as
   * MS but remove the boxes and just write down that info like we had
   * before." True = no boxes and no input boxes: the cover page writes the
   * contractor and customer details out as centred lines (the M+S cover of
   * 2026-08-21, commits 8288f946 + d71c9bd7, before ca3c604e brought the 4
   * boxes back), and the Scope of Work "Monthly" figure prints as text.
   * Default false keeps M+S and CE exactly as they are.
   */
  writtenCover?: boolean
}>

const MSE_COMPANY = {
  // address1/address2 mirror Misc/env/contractCompanies/MSE, the record
  // FirstElectricContract read when it still rendered this contract.
  address1: '6430 Sunset Corporate Dr.',
  address2: 'Las Vegas, NV 89120',
  bidLimit: '$1,000,000',
  email: 'info@mselectriclv.com',
  license: '0078629',
  name: 'M+S Electric',
  phone: '702-545-0980',
  website: 'www.mselectriclv.com',
}

export default React.memo<MSElectricContractProps>(function MSElectricContract({
  company,
  customerID,
  isPDF,
  readOnly,
  toggleSignOwner1,
  toggleSignOwner2,
  toggleSignCompany,
  toggleCancelSignOwner1,
  toggleCancelSignOwner2,
  toggleConfirmEquipmentSign,
  toggleConfirmTotalInitials,
  toggleFinancingInfoSign,
  writtenCover = false,
}) {
  //#region global
  const CO = company ?? MSE_COMPANY
  /**
   * Las clausulas 20 y 21 nombraban "M+S Electric" a pelo en su texto. Con
   * Caballero's renderizando este mismo contrato eso pondria el nombre de otra
   * empresa dentro de las clausulas de un contrato de CE, asi que ahora van
   * como token y se resuelven aqui. Mismo mecanismo que ya usa
   * HomeImprovementAgreement con {{COMPANY_NAME}}.
   */
  const fillCompany = React.useCallback(
    (text: string): string =>
      text
        .replace(/\{\{COMPANY_NAME_UPPER\}\}/g, CO.name.toUpperCase())
        .replace(/\{\{COMPANY_NAME\}\}/g, CO.name),
    [CO.name],
  )
  const [customerData] = r.useCustomer(customerID)

  const {
    customerName,
    customerEmail,
    customerPhone,
    customerAddress,
    homeRep,
    solarCompany,

    security_cams,
    security_cams_show,
    security_cams_customer_cost,
    security_system,
    security_system_show,
    security_system_customer_cost,
    security_tv,
    security_tv_show,
    security_tv_customer_cost,
    security_wifi_cameras,
    security_wifi_cameras_show,
    security_wifi_cameras_customer_cost,
    security_home_automation,
    security_home_automation_show,
    security_home_automation_customer_cost,

    home_improvement_sign_owner1,
    home_improvement_sign_owner1_date,
    home_improvement_sign_owner2,
    home_improvement_sign_owner2_date,
    home_improvement_sign_company,
    home_improvement_sign_company_date,

    home_improvement_cancel_sign_owner1,
    home_improvement_cancel_sign_owner1_date,
    home_improvement_cancel_sign_owner2,
    home_improvement_cancel_sign_owner2_date,

    home_improvement_confirm_equipment_sign,
    home_improvement_confirm_equipment_sign_date,
    home_improvement_confirm_total_initials,
    home_improvement_confirm_total_initials_date,
    home_improvement_financing_info_sign,
    home_improvement_financing_info_sign_date,

    home_improvement_contract_price_touched,
    home_improvement_first_payment_touched,
    home_improvement_second_payment_touched,
  } = customerData

  // A signed contract's figures never move again — not for a total change,
  // not for anything, not even an untouched field (§V). The discrepancy
  // between a frozen split and a changed total stays visible (two numbers on
  // one page that don't add up); silently recomputing a document someone
  // already signed would hide it and leave the customer's paper copy out of
  // sync with no record of when or why.
  const contractSigned = !!(
    home_improvement_sign_owner1 ||
    home_improvement_sign_owner2 ||
    home_improvement_sign_company ||
    home_improvement_confirm_equipment_sign ||
    home_improvement_confirm_total_initials ||
    home_improvement_financing_info_sign
  )

  // Signing v2 (ticket 13213629138): the customer also signs FINANCING
  // INFORMATION. Only for a customer on v2 or with no signature yet, so a
  // contract signed before keeps its layout and pagination (V67).
  const signingV2 = isSigningFlowV2(customerData)

  // No watermark once anything is signed here, cancellation included (V67).
  const watermark = useContractWatermark(
    customerID,
    contractSigned ||
      !!(
        home_improvement_cancel_sign_owner1 ||
        home_improvement_cancel_sign_owner2
      ),
  )

  const { appliedLoan, loanCompanyName, paymentMethodVal, term } =
    r.usePayment(customerID)

  const [owner2NameVal] = r.useCustomerFieldInput(
    customerID,
    'home_improvement_owner2_name',
  )
  const hasOwner2 = !!owner2NameVal

  const [contractPrice, contractPriceWrite] = r.useCustomerFieldInput(
    customerID,
    'home_improvement_contract_price',
  )
  const [firstPayment, firstPaymentWrite] = r.useCustomerFieldInput(
    customerID,
    'home_improvement_first_payment',
  )
  const [secondPayment, secondPaymentWrite] = r.useCustomerFieldInput(
    customerID,
    'home_improvement_second_payment',
  )
  const [projectStreet, projectStreetWrite, projectStreetHandle] =
    r.useCustomerFieldInput(customerID, 'home_improvement_project_street')
  const [projectCity, projectCityWrite, projectCityHandle] =
    r.useCustomerFieldInput(customerID, 'home_improvement_project_city')
  const [projectState, projectStateWrite, projectStateHandle] =
    r.useCustomerFieldInput(customerID, 'home_improvement_project_state')
  const [projectZip, projectZipWrite, projectZipHandle] =
    r.useCustomerFieldInput(customerID, 'home_improvement_project_zip')

  // PROJECT SCOPE box (restored 2026-09-01, see the cover-grid comment). All
  // six fields were dropped in the 8288f946 revert and are being brought back
  // verbatim. `installation_date` is nowhere else in this contract (the only
  // other mention is prose in an INSTALLATION TERMS clause), so it lives here;
  // it renders as an editable input, disabled only in the PDF and the public
  // signing link, same as every other box field.
  const [scopeOther, , scopeOtherHandle] = r.useCustomerFieldInput(
    customerID,
    'home_improvement_scope_other',
  )
  const [installationDate, , installationDateHandle] = r.useCustomerFieldInput(
    customerID,
    'home_improvement_installation_date',
  )
  // Manual "Monthly: $___ /mo" line in the SCOPE OF WORK (Jaron, 2026-10-05,
  // Monday 13213659622). Free text the rep types in — no math, it does not
  // feed TOTAL, the payment split or anything else. Locked once the contract
  // is signed (§V V67), like every other figure on a signed document.
  const [monthlyManual, , monthlyManualHandle] = r.useCustomerFieldInput(
    customerID,
    'home_improvement_monthly_payment',
  )
  // Jaron, Oct 14 (13290658389): "Should have same amount as mthly above. It
  // needs to show the auto mthly but be editable." Until the rep types one,
  // the box shows the applied loan's monthly (the Job Summary's figure);
  // typing stores the lender's exact amount, shared with the Job Summary.
  // Once signed it shows only what was stored (V67): the Job Summary payment
  // signature stores the figure when nothing was typed.
  const monthlyAuto = appliedMonthlyPayment(appliedLoan)
  const monthlyShown =
    monthlyManual ||
    (!contractSigned && monthlyAuto > 0 ? monthlyAuto.toFixed(2) : '')
  const [evCharger, evChargerWrite] = r.useCustomerField(
    customerID,
    'home_improvement_scope_ev_charger',
  )
  const [generator, generatorWrite] = r.useCustomerField(
    customerID,
    'home_improvement_scope_generator',
  )
  const [smartHomeSystem, smartHomeSystemWrite] = r.useCustomerField(
    customerID,
    'home_improvement_scope_smart_home',
  )
  const [smartHomeTouched, smartHomeTouchedWrite] = r.useCustomerField(
    customerID,
    'home_improvement_scope_smart_home_touched',
  )
  const [homePerformance, homePerformanceWrite] = r.useCustomerField(
    customerID,
    'home_improvement_scope_home_performance',
  )

  // Smart Home System is ticked BY DEFAULT (Jaron, 2026-09-01: "por ahora ese
  // siempre marcado por defecto"). Until a rep actually clicks it
  // (`smartHomeTouched === 'yes'`) it renders ticked no matter what the
  // stored value is — 175 existing customers carry a schema-default 'no' that
  // nobody chose. After a click, its real value is honoured.
  const smartHomeChecked =
    smartHomeTouched === 'yes' ? smartHomeSystem === 'yes' : true

  // Jaron/Maria G (Slack, 2026-08-07): "PROJECT LOCATION" must be split into
  // Street/City/State/Zip like the reference PDF — `customerAddress` is a
  // single freeform field (no separate address parts in the schema), so we
  // split it for display only. Missing parts (state/zip are often left off
  // when reps type the address) just render blank, same as the paper form.
  // Seeded once (each field independently) into its own editable/persisted
  // field, same pattern as Total Contract Price below — a rep's manual edit
  // is never overwritten by this running again.
  //
  // Never on a signed contract (V67, pre-deploy QA Nov 1): without this gate
  // just OPENING a signed M+S report wrote the four fields (and re-indexed the
  // customer in Algolia) whenever one was empty, changing a signed document's
  // PROJECT LOCATION on view. Same `contractSigned` gate as the price seed.
  React.useEffect(() => {
    // The written cover (ESR) has no PROJECT LOCATION box to seed.
    if (isPDF || readOnly || writtenCover || contractSigned || !customerAddress)
      return
    // parseCustomerAddress reads the formats reps type ("Street, City ST ZIP",
    // Google's ", USA"); splitAddress put all of that in City or Street. The
    // signed display below keeps splitAddress: it must match live (V67, B199).
    const split = parseCustomerAddress(customerAddress)
    if (!projectStreet && split.street) projectStreetWrite(split.street)
    if (!projectCity && split.city) projectCityWrite(split.city)
    if (!projectState && split.state) projectStateWrite(split.state)
    if (!projectZip && split.zip) projectZipWrite(split.zip)
  }, [
    contractSigned,
    customerAddress,
    isPDF,
    projectCity,
    projectCityWrite,
    projectState,
    projectStateWrite,
    projectStreet,
    projectStreetWrite,
    projectZip,
    projectZipWrite,
    readOnly,
    writtenCover,
  ])

  // What a signed contract shows in PROJECT LOCATION when a part was never
  // stored (director, pre-deploy checks Nov 2: Cecilia Celis, CE signed).
  // Live wrote the split address on view (the seed above, before its signed
  // gate), so those contracts always showed it; now nothing is written, so
  // the same split is shown instead: display only, never saved, in every
  // mode (screen, PDF, signing link). A stored value always wins. Unsigned
  // contracts are unchanged: the seed above still stores the split.
  const signedProjectSplit =
    contractSigned && customerAddress ? c.splitAddress(customerAddress) : null
  const projectShown = (
    local: string,
    stored: string | undefined,
    part: 'city' | 'state' | 'street' | 'zip',
  ): string =>
    local || (signedProjectSplit && !stored ? signedProjectSplit[part] : '')
  const projectStreetShown = projectShown(
    projectStreet,
    customerData.home_improvement_project_street,
    'street',
  )
  const projectCityShown = projectShown(
    projectCity,
    customerData.home_improvement_project_city,
    'city',
  )
  const projectStateShown = projectShown(
    projectState,
    customerData.home_improvement_project_state,
    'state',
  )
  const projectZipShown = projectShown(
    projectZip,
    customerData.home_improvement_project_zip,
    'zip',
  )

  // Jaron/Maria G, Slack 2026-07-22 (SPEC T56, verbatim): "we can only
  // mention the types of cameras we are putting in... anything with Alarms we
  // cannot mention so we need to automatically not include those. So
  // everything needs to add up for cameras, homebase things like that to the
  // price." Three instructions - name the cameras, hide the alarm items, keep
  // the total whole.
  //
  // This hook is that rule, already built and already in use on Caballero and
  // First Electric: it itemizes security_cams, security_tv,
  // security_wifi_cameras and security_home_automation by name (with
  // alarm-safe renames, e.g. "Solar S4 Camera" -> "eufy S4 Wireless Camera")
  // and excludes only security_system, whose product names literally say
  // alarm. MS Electric was the one contract not using it.
  const {
    collapsed: scopeCollapsed,
    collapsedHandleMax: scopeCollapsedHandleMax,
    collapsedHandleMin: scopeCollapsedHandleMin,
    detailLines: securityScopeLines,
  } = r.useSecurityScopeDetailLines(customerID)

  // Maria G/Jaron (Slack, 2026-08-04): the itemized total was undercounting
  // because alarm-category equipment was excluded from the visible line
  // items entirely. Every active security category is grouped into a single
  // "Smart Home" line here (never named/described individually) so the
  // web-only Scope of Work total reconciles with the real grand total.
  const smartHomePrice = React.useMemo((): number => {
    const categories = [
      [security_cams, security_cams_show, security_cams_customer_cost],
      [security_system, security_system_show, security_system_customer_cost],
      [security_tv, security_tv_show, security_tv_customer_cost],
      [
        security_wifi_cameras,
        security_wifi_cameras_show,
        security_wifi_cameras_customer_cost,
      ],
      [
        security_home_automation,
        security_home_automation_show,
        security_home_automation_customer_cost,
      ],
    ]
    return categories.reduce(
      (sum, [enabled, show, cost]) =>
        enabled === 'no' || show === 0 ? sum : sum + c.parseFloating(cost),
      0,
    )
  }, [
    security_cams,
    security_cams_show,
    security_cams_customer_cost,
    security_system,
    security_system_show,
    security_system_customer_cost,
    security_tv,
    security_tv_show,
    security_tv_customer_cost,
    security_wifi_cameras,
    security_wifi_cameras_show,
    security_wifi_cameras_customer_cost,
    security_home_automation,
    security_home_automation_show,
    security_home_automation_customer_cost,
  ])

  // Everything on the job that is NOT security equipment, as its own lines.
  //
  // Without these the page listed "Smart Home" and then a TOTAL charging for
  // the whole job, so an HVAC or roof customer saw thousands of dollars in the
  // total that nothing above it accounted for. Same two conditions as
  // smartHomePrice, so the new lines and the old one agree.
  // The Smart Home line goes FIRST, ahead of the non-security ones: it stands
  // in for the itemised block directly above it, so it reads as the close of
  // that section rather than as one more home-improvement product. See
  // `smartHomeScopeLine` for why the alarm's money had no line at all.
  const otherScopeLines = React.useMemo((): readonly c.ScopeLine[] => {
    const others = c.nonSecurityScopeLines(customerData)
    const smartHome = c.smartHomeScopeLine(customerData)

    return smartHome ? [smartHome, ...others] : others
  }, [customerData])
  const airConditionerDetails = c.airConditionerScopeDetailLines(customerData)

  // Full customer total (same reconciliation rule as
  // HomeImprovementAgreement.tsx/FirstElectricContract.tsx — see B72: TOTAL
  // must always use the full grand total, never a reduced/itemized-only sum,
  // so it matches Monthly Payment below it). Also the seed source for
  // `contractPrice`/`firstPayment`/`secondPayment` below — see the effect a
  // few lines down.
  const totalCost = r.useTotalCost('customer', customerID)

  // Keep Total Contract Price and the 50/50 split (SCHEDULE OF PROGRESS
  // PAYMENTS, page 2 of the reference PDF) in step with the same grand total
  // Scope of Work/TOTAL uses.
  //
  // The old version wrote each field ONCE (`if (!firstPayment)`) with
  // `totalCost` still in the deps, so once seeded it never moved again even
  // as the total changed — the split froze at half of the FIRST total the
  // customer ever had, and page 1's TOTAL and page 2's schedule could then
  // disagree by thousands (2026-09-01, ticket 12915736128; two signed MSE
  // contracts were already ~$4-5k out).
  //
  // Now: while the contract is UNSIGNED, a field that is empty or was never
  // hand-edited (`*_touched !== 'yes'`) re-tracks the current total; a
  // hand-edited field is respected. Once SIGNED, nothing here writes ever
  // again (see §V and `contractSigned` above). "Add Service(s) Price" has no
  // source anywhere in the app and stays purely manual regardless.
  React.useEffect(() => {
    if (isPDF || readOnly || !totalCost || contractSigned) return
    const whole = totalCost.toFixed(2)
    const half = (totalCost / 2).toFixed(2)
    if (
      home_improvement_contract_price_touched !== 'yes' &&
      contractPrice !== whole
    ) {
      contractPriceWrite(whole)
    }
    if (
      home_improvement_first_payment_touched !== 'yes' &&
      firstPayment !== half
    ) {
      firstPaymentWrite(half)
    }
    if (
      home_improvement_second_payment_touched !== 'yes' &&
      secondPayment !== half
    ) {
      secondPaymentWrite(half)
    }
  }, [
    contractPrice,
    contractPriceWrite,
    contractSigned,
    firstPayment,
    firstPaymentWrite,
    home_improvement_contract_price_touched,
    home_improvement_first_payment_touched,
    home_improvement_second_payment_touched,
    isPDF,
    readOnly,
    secondPayment,
    secondPaymentWrite,
    totalCost,
  ])

  // TOTAL (Scope of Work, below) reads the SAME frozen field the effect
  // above seeds Initial Amount and the SCHEDULE OF PROGRESS PAYMENTS from —
  // `home_improvement_contract_price` — instead of recomputing `totalCost`
  // live. Luis, 2026-09-03 (Ciara Wilson, ticket re: Maria G): once a
  // customer's `_customer_cost` fields drift down from what the loan was
  // actually applied against (has_touched_customer_cost reset — see V64),
  // `totalCost` recomputes smaller than the financed amount, while
  // `contractPrice`/`firstPayment`/`secondPayment` stay frozen at the
  // financed total (once signed, V67 stops them moving at all). That left
  // TOTAL as the one number on the page disagreeing with Initial Amount and
  // page 2's schedule, which both already read the frozen fields. Falls
  // back to `totalCost` only for the one render before the seed effect has
  // written `contractPrice` yet (brand-new customer, still 0/empty).
  const scopeOfWorkTotal = c.parseFloating(contractPrice) || totalCost
  // Display only: the seed above keeps writing exactly what it wrote.
  const totalPending = totalCostPendingApply(customerData, c.IS_MAIN)

  // Monthly Payment was removed from both the Scope of Work and the Financing
  // Information section (Jaron, 2026-08-25 meeting, ticket 12915736128), so no
  // monthly figure is computed here any more.

  const buttonSignatureStyle = React.useMemo(
    () => ({
      backgroundColor: c.themeTuple.light.paper.highlight,
      width: 256,
    }),
    [],
  )

  // MSE clause bodies below are never templated with {{COMPANY_NAME}} — this
  // contract only ever renders for M+S Electric, so the name is hardcoded
  // literally (matches the reference PDF word-for-word, including clause 20
  // and 21 naming "M+S Electric" directly instead of a token).
  //#endregion

  // Both dates on this cover page resolve from here - the Purchase
  // Agreement line at the top and the "On this ___ day of ___" line
  // further down - so they can never disagree with each other.
  const agreementDate =
    home_improvement_sign_owner1 || home_improvement_sign_company
      ? home_improvement_sign_owner1_date || home_improvement_sign_company_date
      : Date.now()

  const header = (
    <div>
      <p className={sharedStyles['heading']}>{CO.name}</p>
    </div>
  )

  const checkbox = (
    checked: boolean,
    label: string,
    onToggle: () => void,
  ): React.ReactNode => (
    <span
      className={styles['checkbox-item']}
      onClick={isPDF || readOnly ? undefined : onToggle}
    >
      ({checked ? 'X' : ' '}) {label}
    </span>
  )

  return (
    <>
      {/* No companyBorder (top/bottom color stripe) anywhere in this
          component — Maria G (Slack, 2026-08-05): "quita para ellos hasta
          las rayas naranjas por fa". Fernando's reference PDF has no such
          border on any page. */}
      <ReportPages customerID={customerID} hPad watermark={watermark}>
        <div style={sx.logo}>
          <ContractLogo isPDF={isPDF} readOnly={readOnly} size="l" />
        </div>

        {gapBig}

        <p style={sx.title}>HOME IMPROVEMENT AGREEMENT</p>

        {gapMedium}

        {/* Cover page history, three successive decisions — not a conflict:
            1) 2026-08-05 (be3df503): built as the boxed grid from Fernando
               Sabbadini's reference PDF — 6 boxes: CONTRACTOR'S INFORMATION,
               CUSTOMER'S INFORMATION, SYSTEM PRICING, PROJECT LOCATION,
               PROJECT SCOPE DESCRIPTION, PROJECT SCOPE.
            2) 2026-08-20 (8288f946): Jaron killed the whole grid ("I NEED
               THIS FIXED TODAY", screenshot of this page) and put the plain
               Purchase Agreement opening back. All 6 boxes gone.
            3) 2026-08-25 meeting (Luis notes 23:35, ticket 12915736128):
               "Son 4 cajas de las 6" — bring back CONTRACTOR'S INFORMATION,
               CUSTOMER'S INFORMATION, PROJECT LOCATION and PROJECT SCOPE
               ("el sexto"). SYSTEM PRICING and PROJECT SCOPE DESCRIPTION
               stay out — they held the only two dead fields,
               `home_improvement_add_service_price` and
               `home_improvement_project_scope_description` (both 0 non-empty
               across 7,160 customers, verified 2026-09-01), so nothing is
               lost by dropping them.

            The grid box contents are verbatim from the pre-8288f946 code.
            Field-by-field de-dup with the centred blocks Jaron approved
            2026-08-21 (Luis, 2026-09-01: "la información que sea duplicada la
            quitas de lo centrado, la dejas en el cuadrado si lo tiene"):
            CONTRACTOR'S INFORMATION now carries name/phone/email/website/
            licence/bid-limit, CUSTOMER'S INFORMATION carries name/email/
            phone, so those lines come out of the centred blocks. What each
            box does NOT carry stays centred — the company street address.
            The Buyer / Co-Buyer / Property Address block further down is
            untouched (approved 2026-08-21 02:09).

            The "CUSTOMER INFORMATION" heading + customer address that used
            to sit here (Fernando's "customer info in the title") is gone
            (12898820318 follow-up, Luis on his own downloaded PDF of
            Sharlene Yang: the heading printed overlapping the address text
            on the same line). By the time the 4 boxes came back it was pure
            duplication anyway — name/email/phone are in CUSTOMER'S
            INFORMATION below, the address is in PROJECT LOCATION — so
            nothing is lost by dropping it, only the overlap. */}
        {writtenCover ? (
          // ESR (13376550851): "remove the boxes and just write down that info
          // like we had before" — the 2026-08-21 M+S cover (pre-ca3c604e):
          // company lines centred, then CUSTOMER INFORMATION centred. The
          // company name heads the block because ESR has no logo yet and the
          // CONTRACTOR'S INFORMATION box that carried it is not drawn here.
          // PROJECT LOCATION / PROJECT SCOPE were not on that cover either;
          // the address is written below and again in Property Address.
          // The heading is nowrap: the PDF capture once wrapped it onto the
          // address line (47a8d976).
          <div data-testid="written-cover" style={gs.flowRootWidth100}>
            <p style={sx.partyHeading}>{CO.name}</p>
            {!!CO.address1 && (
              <p style={sx.companyInfoCentered}>{CO.address1}</p>
            )}
            {!!CO.address2 && (
              <p style={sx.companyInfoCentered}>{CO.address2}</p>
            )}
            <p style={sx.companyInfoCentered}>{CO.email}</p>
            <p style={sx.companyInfoCentered}>License #: {CO.license}</p>
            <p style={sx.companyInfoCentered}>Phone: {CO.phone}</p>
            {!!CO.website && <p style={sx.companyInfoCentered}>{CO.website}</p>}
            {!!CO.bidLimit && (
              <p style={sx.companyInfoCentered}>Bid Limit: {CO.bidLimit}</p>
            )}

            {gapSmall}

            <p style={sx.partyHeading}>CUSTOMER INFORMATION</p>
            <p style={sx.companyInfoCentered}>{customerName}</p>
            {!!customerAddress && (
              <p style={sx.companyInfoCentered}>{customerAddress}</p>
            )}
            {!!customerEmail && (
              <p style={sx.companyInfoCentered}>{customerEmail}</p>
            )}
            {!!customerPhone && (
              <p style={sx.companyInfoCentered}>Phone: {customerPhone}</p>
            )}
          </div>
        ) : (
          <>
            {!!CO.address1 && (
              <p style={sx.companyInfoCentered}>{CO.address1}</p>
            )}
            {!!CO.address2 && (
              <p style={sx.companyInfoCentered}>{CO.address2}</p>
            )}
          </>
        )}

        {gapMedium}

        {!writtenCover && (
          <div className={styles['cover-grid']}>
            <div className={styles['box']}>
              <p className={styles['box-header']}>CONTRACTOR'S INFORMATION</p>
              <p className={styles['box-line']}>{CO.name}</p>
              <p className={styles['box-line']}>Phone: {CO.phone}</p>
              <p className={styles['box-line']}>Email: {CO.email}</p>
              {!!CO.website && (
                <p className={styles['box-line']}>Website: {CO.website}</p>
              )}
              <p className={styles['box-line']}>
                Contractor Lic #: {CO.license}
              </p>
              {!!CO.bidLimit && (
                <p className={styles['box-line']}>Bid Limit: {CO.bidLimit}</p>
              )}
            </div>

            <div className={styles['box']}>
              <p className={styles['box-header']}>CUSTOMER'S INFORMATION</p>
              <p className={styles['box-line']}>Name(s): {customerName}</p>
              <p className={styles['box-line']}>
                Email Address: {customerEmail}
              </p>
              <p className={styles['box-line']}>Phone: {customerPhone}</p>
            </div>

            <div className={styles['box']}>
              <p className={styles['box-header']}>PROJECT LOCATION</p>
              <p className={styles['box-line']}>
                Street:{' '}
                <input
                  className={sharedStyles['input-inline']}
                  disabled={isPDF || readOnly}
                  onChange={projectStreetHandle}
                  value={projectStreetShown}
                />
              </p>
              <p className={styles['box-line']}>
                City:{' '}
                <input
                  className={sharedStyles['input-inline']}
                  disabled={isPDF || readOnly}
                  onChange={projectCityHandle}
                  value={projectCityShown}
                />
              </p>
              <p className={styles['box-line']}>
                State:{' '}
                <input
                  className={sharedStyles['input-inline']}
                  disabled={isPDF || readOnly}
                  onChange={projectStateHandle}
                  value={projectStateShown}
                />
              </p>
              <p className={styles['box-line']}>
                Zip Code:{' '}
                <input
                  className={sharedStyles['input-inline']}
                  disabled={isPDF || readOnly}
                  onChange={projectZipHandle}
                  value={projectZipShown}
                />
              </p>
            </div>

            <div className={styles['box']}>
              <p className={styles['box-header']}>PROJECT SCOPE</p>
              <p className={styles['box-line']}>Product Type:</p>
              <div className={styles['checkbox-row']}>
                {checkbox(evCharger === 'yes', 'EV Charger', () =>
                  evChargerWrite(evCharger === 'yes' ? 'no' : 'yes'),
                )}
                {checkbox(generator === 'yes', 'Generator', () =>
                  generatorWrite(generator === 'yes' ? 'no' : 'yes'),
                )}
                {/* Smart Home System — ticked by default (see `smartHomeChecked`
                  above). Clicking flips the shown state and marks the box
                  touched so the choice sticks. The other three default off. */}
                {checkbox(smartHomeChecked, 'Smart Home System', () => {
                  smartHomeSystemWrite(smartHomeChecked ? 'no' : 'yes')
                  smartHomeTouchedWrite('yes')
                })}
                {checkbox(homePerformance === 'yes', 'Home Performance', () =>
                  homePerformanceWrite(
                    homePerformance === 'yes' ? 'no' : 'yes',
                  ),
                )}
              </div>
              <p className={styles['box-line']}>
                Other:{' '}
                <input
                  className={sharedStyles['input-inline']}
                  disabled={isPDF || readOnly}
                  onChange={scopeOtherHandle}
                  value={scopeOther}
                />
              </p>
              <p className={styles['box-line']}>
                Installation Date:{' '}
                <input
                  className={sharedStyles['input-inline']}
                  disabled={isPDF || readOnly}
                  onChange={installationDateHandle}
                  value={installationDate}
                />
              </p>
            </div>
          </div>
        )}

        {!writtenCover && gapMedium}

        <p style={sx.node}>
          This is a Purchase Agreement ("PA") on this Date{' '}
          <span className={sharedStyles['underlined']}>
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
            {c.formatContractDate(agreementDate, solarCompany)}
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
          </span>{' '}
          between {CO.name} ("Company") and:
        </p>

        {gapMedium}

        <p style={sx.node}>
          Buyer:{' '}
          <span className={sharedStyles['underlined']}>
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
            {customerName}
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
          </span>{' '}
          Co-Buyer:{' '}
          <span className={sharedStyles['underlined']}>
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
            {owner2NameVal}
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
          </span>
        </p>

        <p style={sx.node}>
          Property Address:{' '}
          <span className={sharedStyles['underlined']}>
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
            {customerAddress}
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
          </span>
        </p>

        <p style={sx.node}>
          Email:{' '}
          <span className={sharedStyles['underlined']}>
            &nbsp;&nbsp;&nbsp;&nbsp;{customerEmail}&nbsp;&nbsp;&nbsp;&nbsp;
          </span>{' '}
          Phone:{' '}
          <span className={sharedStyles['underlined']}>
            &nbsp;&nbsp;&nbsp;&nbsp;{customerPhone}&nbsp;&nbsp;&nbsp;&nbsp;
          </span>
        </p>

        {/* Now prints in the downloaded PDF too (12898820318, Jaron: "there
            is no full breakdown of the equipment"). Was gated `!isPDF` since
            this component's introduction (be3df503, 2026-08-05) — nobody had
            asked for that exclusion, it just carried over when MS Electric
            got its own dedicated contract. Removed 2026-09-04.

            Smart Home is a CATEGORY with its equipment listed under it, not a
            single lump sum. Jaron circled the old single line on 2026-08-21
            02:04 with "FIX THIS", and he was right: collapsing every category
            into one line satisfied "everything needs to add up" (the reason
            it was done, see the smartHomePrice comment above) at the cost of
            "we can only mention the types of cameras we are putting in",
            which was part of the same instruction. Naming them costs nothing:
            useSecurityScopeDetailLines already excludes security_system (the
            one category whose product names say "alarm"), and TOTAL below is
            the full grand total, so the alarm equipment stays charged without
            ever being named.

            Rendered for any work at all, not just security (Samuel, 26015084):
            it used to be `smartHomePrice > 0`, so a customer buying only an
            HVAC system or a roof got a contract with no SCOPE OF WORK section
            at all — no line items and no TOTAL.

            The TOTAL below is `scopeOfWorkTotal` — the same frozen financed
            amount Initial Amount/page 2 use (see the effect above), not a
            live recompute of the equipment total. Summing only the visible
            lines is the B72 mistake: it undercounts by exactly the
            security_system total. Reading `totalCost` directly had the same
            failure mode one level up — it undercounts by whatever the loan
            was actually applied for on top of raw equipment cost (lead fee +
            commission + dealer fee, see V16/V25/V38) once that fee stack
            drifts out of `_customer_cost` (Ciara Wilson, 2026-09-03).
            (Monthly Payment used to be printed right under the TOTAL and had
            to reconcile with it; it was removed here per the 2026-08-25
            meeting, ticket 12915736128.)

            **2026-09-03 correction (Luis, live on Ciara Wilson):** a same-day
            attempt at fixing the "Smart Home" label collision renamed the
            flat alarm-cost line below (`smartHomeScopeLine`) to "Security
            System" — wrong, per Luis: the word "security" itself can't
            appear on the page (that's the whole point of T56's alarm-safe
            wording), category name or not. Reverted that label back to
            "Smart Home". The actual fix for the collision is instead
            removing the heading text above the itemized lines (see the
            comment right above `<DetailHeader>` below) — one "Smart Home" on
            the page, not a relabeled second one. */}
        {(securityScopeLines.length > 0 ||
          smartHomePrice > 0 ||
          otherScopeLines.length > 0) && (
          <>
            {gapMedium}

            <p className={sharedStyles['subheading']}>SCOPE OF WORK</p>

            {gapSmall}

            {(securityScopeLines.length > 0 ||
              airConditionerDetails.length > 0) && (
              <>
                {/* No "Smart Home" heading here any more (Luis, 2026-09-03,
                      live on Ciara Wilson right after the rename below shipped):
                      it duplicated the literal text of the flat alarm-cost
                      line further down (`smartHomeScopeLine`), and having
                      both on the page read as a subtotal mismatch that
                      wasn't real. The itemized lines below speak for
                      themselves without a category heading. */}
                <DetailHeader isPDF={isPDF} />
                {securityScopeLines.map(({ id, name, notes, price, qty }) => (
                  <DetailLine
                    collapsed={!!scopeCollapsed[id]}
                    id={id}
                    isPDF={isPDF}
                    key={id}
                    name={name}
                    notes={notes}
                    onCollapse={scopeCollapsedHandleMin[id]!}
                    onExpand={scopeCollapsedHandleMax[id]!}
                    price={price}
                    qty={qty}
                    readOnly={readOnly}
                  />
                ))}

                {gapSmall}
              </>
            )}

            {otherScopeLines.map(({ key, label, price }) => {
              if (key === 'security_system')
                return (
                  // Smart Home, as a category divider in the same columns as
                  // WiFi Camera: bold, centered, its price under Unit/Total
                  // Cost. Jaron asked for this three times (2026-09-07, 09-10,
                  // 09-22 — Monday 13019072606 / 12997734952). Still no product
                  // lines under it: T56, "at the same price, no descriptions".
                  <DetailPlainLine
                    bold
                    isPDF={isPDF}
                    key={key}
                    label={smartHomeDivider}
                    price={price}
                  />
                )

              const airConditioner = airConditionerDetails.find(
                (line) => line.id === key,
              )
              if (airConditioner)
                return (
                  <div
                    key={key}
                    style={{
                      ...gs.flowRootWidth100,
                      breakInside: 'avoid',
                      pageBreakInside: 'avoid',
                    }}
                  >
                    <DetailLine
                      collapsed={!!scopeCollapsed[airConditioner.id]}
                      id={airConditioner.id}
                      isPDF={isPDF}
                      name={airConditioner.name}
                      notes={airConditioner.notes}
                      onCollapse={scopeCollapsedHandleMin[airConditioner.id]!}
                      onExpand={scopeCollapsedHandleMax[airConditioner.id]!}
                      price={airConditioner.price}
                      qty={airConditioner.qty}
                      readOnly
                    />
                  </div>
                )

              return (
                <p key={key} style={sx.scopeLine}>
                  <span>{label}</span>
                  <span>
                    {Intl.NumberFormat('en-US', {
                      currency: 'USD',
                      maximumFractionDigits: 2,
                      minimumFractionDigits: 2,
                      style: 'currency',
                    }).format(price)}
                  </span>
                </p>
              )
            })}

            {gapSmall}

            {/* Manual Monthly line (13213659622, see `monthlyManual` above).
                  Hidden when it can't be edited and is empty, so contracts
                  signed before this line existed — and their PDFs — render
                  exactly as before. */}
            {!isCashPayment({ paymentMethod: paymentMethodVal }) &&
              !(
                (writtenCover || isPDF || readOnly || contractSigned) &&
                !monthlyShown
              ) && (
                <p style={sx.scopeLine}>
                  <span>Monthly:</span>
                  {writtenCover ? (
                    // ESR: no input boxes; written figure only (stored manual
                    // value, else the applied loan's monthly while unsigned).
                    <span>${monthlyShown} /mo</span>
                  ) : (
                    <span>
                      $
                      <input
                        className={sharedStyles['input-inline']}
                        disabled={isPDF || readOnly || contractSigned}
                        onChange={monthlyManualHandle}
                        value={monthlyShown}
                      />{' '}
                      /mo
                    </span>
                  )}
                </p>
              )}

            {/* There was an "Initial Amount $X" box here (Jaron, 2026-08-25
                  meeting: "quitar Monthly Payment del scope of work, colocar
                  en vez el monto inicial en un cuadrado"). Jaron reversed that
                  on 2026-09-01 — Slack screenshot, red X over the box, "Please
                  remove initial amount" — so the box is gone. §B B145: client
                  criterion change, NOT a bug; do not re-add it.

                  The FIELD it showed (`firstPayment` /
                  `home_improvement_first_payment`) and the effect that seeds
                  it (B144 / e407eb65) stay: the SCHEDULE OF PROGRESS PAYMENTS
                  table on page 2 still reads "First Payment 50% at Contract
                  Sign $X" from it. Only the Scope-of-Work box was removed. */}

            {/* Scope-of-Work sign band (ticket 12915736128). Jaron
                  2026-09-01: no more big signature box + small square — TWO
                  plain BUTTONS, both taking initials, both opening the canvas
                  modal. "Confirm Equipment" sits in the middle; the TOTAL
                  goes back to the right with "Confirm Total" as a button
                  directly beneath it. Styled like the app's SAVE / CLEAR
                  DRAWING buttons (`variant="contained"` — solid fill, white
                  uppercase, rounded) — no bespoke style.

                  This walks back the square (B143) and the one-row-axis pass
                  (2026-09-01 earlier today): all of that was the shape/size/
                  position of a box that no longer exists.

                  Neither button is `readOnly`-gated: the customer is who
                  presses them during the public signing flow (Luis,
                  2026-08-25: "Cliente es"; V43's gate is about editing
                  contract data, not signing it). Once a field is set, its
                  stored initials image + date show in place of the button
                  (the web report is the archived signed copy, B138) — that
                  branch is what the PDF shows for a signed contract. An
                  UNSIGNED contract downloaded as PDF gets a blank `<div />`
                  instead of a clickable button, same pattern already used
                  for the signature buttons further down this file
                  (12898820318 follow-up, Samuel: don't ship a live button
                  into a document the customer downloads). */}
            <div style={sx.scopeSignRow}>
              {/* CENTRE — Confirm Equipment. Was a full signature; now
                    initials, via its own field. */}
              <div style={sx.confirmBtnBlock}>
                {home_improvement_confirm_equipment_sign ? (
                  <>
                    <p style={sx.confirmBoxLabel}>Confirm Equipment</p>
                    <img
                      alt=""
                      className={sharedStyles['signature-img']}
                      src={home_improvement_confirm_equipment_sign}
                      style={sx.placedInitials}
                    />
                    <p style={sx.node}>
                      {c.formatContractDate(
                        home_improvement_confirm_equipment_sign_date,
                        solarCompany,
                        'MM/DD/YYYY - hh:mma',
                      )}
                    </p>
                  </>
                ) : isPDF || signingV2 ? (
                  // Jaron, Oct 14 ("Remove", red X over the button): a v2
                  // agreement no longer asks for Confirm Equipment; the
                  // Job Summary's "Verify Equipment" covers it. Contracts
                  // already initialed keep showing their initials above.
                  <div />
                ) : (
                  <Button
                    data-guided-signature="home_improvement_confirm_equipment_sign"
                    onClick={toggleConfirmEquipmentSign}
                    variant="contained"
                  >
                    Confirm Equipment
                  </Button>
                )}
              </div>

              {/* RIGHT — TOTAL, then the Confirm Total button directly under
                    it. */}
              <div style={sx.totalRight}>
                <span style={sx.totalAmount}>
                  TOTAL: {c.blankChar(12)}
                  {totalPending
                    ? TOTAL_PENDING_LABEL
                    : Intl.NumberFormat('en-US', {
                        currency: 'USD',
                        maximumFractionDigits: 2,
                        minimumFractionDigits: 2,
                        style: 'currency',
                      }).format(scopeOfWorkTotal)}
                </span>

                {home_improvement_confirm_total_initials ? (
                  <>
                    <img
                      alt=""
                      className={sharedStyles['signature-img']}
                      src={home_improvement_confirm_total_initials}
                      style={sx.placedInitials}
                    />
                    <p style={sx.node}>
                      {c.formatContractDate(
                        home_improvement_confirm_total_initials_date,
                        solarCompany,
                        'MM/DD/YYYY',
                      )}
                    </p>
                  </>
                ) : isPDF ? (
                  <div />
                ) : (
                  <Button
                    data-guided-signature="home_improvement_confirm_total_initials"
                    onClick={toggleConfirmTotalInitials}
                    variant="contained"
                  >
                    Confirm Total
                  </Button>
                )}
              </div>
            </div>
          </>
        )}

        {gapMedium}

        {/* Wrapped in one flow-root div (12898820318 follow-up, live on
            Sharlene Yang's downloaded PDF, Jaron: "FINANCING INFORMATION"
            printed with nothing under it, its own content pushed onto the
            next page). Root cause: the heading and its content were loose
            siblings inside `<ReportPages>`, which measures and paginates
            each child independently (see that component's own doc-comment —
            "Use a wrapping div with display: 'flow-root' to group together
            elements that should not be split across pages"). Grouping them
            makes the pair a single unit for pagination purposes: it now
            either fits together on the current page or moves together to
            the next one, but never splits. */}
        <div style={gs.flowRootWidth100}>
          <p className={sharedStyles['subheading']}>FINANCING INFORMATION</p>

          {gapSmall}

          {paymentMethodVal === 'financing' ? (
            <>
              {/* "Estimated monthly payment" removed and the term now stated
                  in MONTHS, not years (Jaron, 2026-08-25 meeting, ticket
                  12915736128: "Quitar Monthly Payment de la sección. El plazo
                  va en MESES, no en años"). */}
              {/* Order per Jaron 2026-09-10 (13019072606): company, term,
                  rate — lines 1 and 3 swapped. */}
              <p style={sx.node}>1. Financing company: {loanCompanyName}.</p>
              <p style={sx.node}>
                2. The Length of the term of the agreement is{' '}
                {(term?.years ?? 0) * 12} months.
              </p>
              <p style={sx.node}>
                3. Interest Rate approved:{' '}
                {((term?.interest ?? 0) * 100).toFixed(2)}%.
              </p>
            </>
          ) : (
            <p style={sx.node}>N/A — Cash Payment selected.</p>
          )}

          {/* Signing v2 (13213629138): the customer signs the financing
              terms, inside this flow-root so heading, terms and signature
              move between pages together (V47). Only when there are terms
              to sign (financing). Customer-signed, so not readOnly-gated,
              same as Confirm Total; an unsigned PDF gets nothing. */}
          {signingV2 &&
            (home_improvement_financing_info_sign ||
              paymentMethodVal === 'financing') && (
              <div style={sx.financingSignBlock}>
                {home_improvement_financing_info_sign ? (
                  <>
                    <p style={sx.confirmBoxLabel}>Financing Information</p>
                    <img
                      alt=""
                      className={sharedStyles['signature-img']}
                      src={home_improvement_financing_info_sign}
                      style={sx.placedInitials}
                    />
                    <p style={sx.node}>
                      {c.formatContractDate(
                        home_improvement_financing_info_sign_date,
                        solarCompany,
                        'MM/DD/YYYY - hh:mma',
                      )}
                    </p>
                  </>
                ) : isPDF ? (
                  <div />
                ) : (
                  <Button
                    data-guided-signature="home_improvement_financing_info_sign"
                    onClick={toggleFinancingInfoSign}
                    variant="contained"
                  >
                    {/* Jaron, Oct 14: "Confirm finance terms" */}
                    Confirm Finance Terms
                  </Button>
                )}
              </div>
            )}
        </div>

        {gapSmall}

        <p className={sharedStyles['subheading']}>IMPORTANT INFORMATION</p>

        {gapSmall}

        <p style={sx.node}>
          1. Please refer to Page 6 of this agreement for Recovery Fund
          Administered by the State Contractor's Board information pursuant to
          NRS 624.470.
        </p>

        {gapMedium}

        {/* Day / month / year fill themselves from the signature date, the
            same source the "This is a Purchase Agreement on this Date"
            line at the top of this page already used.

            Why they were blank until now: this sentence came in with
            be3df503, which transcribed Fernando's reference PDF word for
            word. On paper those underscores are where the owner writes
            with a pen, so nothing was wired to them. The line does not
            exist in any other contract - before Aug 5 MS Electric rendered
            through FirstElectricContract, which has no such sentence - so
            this was never a regression, just a paper form copied literally.
            The page ended up with two dates behaving differently, which
            nobody chose. Jaron asked about it on 2026-08-21 02:09 ("Are we
            supposed to write in the date"); Luis's call was to make it
            match the one above. */}
        <p style={sx.node}>
          On this{' '}
          <span className={sharedStyles['underlined']}>
            &nbsp;&nbsp;
            {c.formatContractDate(agreementDate, solarCompany, 'Do')}
            &nbsp;&nbsp;
          </span>{' '}
          day of{' '}
          <span className={sharedStyles['underlined']}>
            &nbsp;&nbsp;
            {c.formatContractDate(agreementDate, solarCompany, 'MMMM')}
            &nbsp;&nbsp;
          </span>
          ,{' '}
          <span className={sharedStyles['underlined']}>
            &nbsp;&nbsp;
            {c.formatContractDate(agreementDate, solarCompany, 'YYYY')}
            &nbsp;&nbsp;
          </span>{' '}
          ("The Owner(s)") {customerName}, agree on the Scheduled Payments and
          the Terms and Conditions listed below:
        </p>

        {gapMedium}

        <p className={sharedStyles['subheading']}>
          SCHEDULE OF PROGRESS PAYMENTS
        </p>

        {gapSmall}

        <p style={sx.node}>
          ({paymentMethodVal === 'financing' ? 'X' : ' '}) Third Party
          Financing: You are entering in agreement to purchase a HOME
          IMPROVEMENT unit. Certain of the rights and obligations of this
          Contract are subject to the Owner receiving third-party financing for
          the cost of the Installation.
        </p>
        <p style={sx.node}>
          ({paymentMethodVal === 'cash' ? 'X' : ' '}) Cash Payment: Owner will
          pay contractor the contract price according to the following scheduled
          payments:
        </p>

        {gapSmall}

        {/* Maria G (Slack 2026-08-07): "Debe verse así" — pointed at page 2
            of Fernando's reference PDF, a real bordered table, not the
            First/Second Payment line folded into the paragraph above. */}
        {/* No dollar column: the original paper contract prints only the
            percentages here, not a figure. Jaron flagged the added amount
            column (circled it) — restored to match the paper original. */}
        {/* Cash-only (Myriam, 12898820318 follow-up, live on Sharlene Yang —
            financing selected, GoodLeap 6.99%/180mo — but this table printed
            underneath anyway): this table is the Cash Payment paragraph's own
            schedule, not a generic one. Proof it's cash-specific and not a
            default every method falls back to: the sibling contract
            (HomeImprovementAgreement.tsx / Caballero's) carries this exact
            same "50% at Contract Sign / 50% at Job Completion" wording
            inline, INSIDE the Cash Payment sentence, and prints nothing at
            all for financing. There is also no schedule/down-payment
            structure anywhere in the financing data model
            (`PayMethodApplied` only ever carries loanCompanyName/offerName/
            planName/termInterest/termYears) — a financed deal has nothing
            of this shape to show, it isn't a gap to fill from real data.

            The 50%/50% itself is not an example value to source from a
            field: it is the company's literal cash-payment policy text,
            verbatim from the paper reference contract, word-for-word
            identical to Caballero's. What it computes from real per-deal
            data is already correct — `contractPrice` (the frozen total this
            table's percentages describe) comes straight from the customer's
            actual `totalCost`, no dollar figure is printed any more (see the
            comment above), and nothing here is hand-typed.

            `paymentMethodVal` defaults to 'financing' when nothing was ever
            picked (react-utils.tsx usePayment, `?? 'financing'`) — there is
            no third, blank-method state this page ever renders, so
            `=== 'cash'` and `!== 'financing'` are equivalent today. Checked
            live: 45 of 61 MSE customers have never picked a method and are
            silently defaulting to financing everywhere on this page
            (checkbox AND Financing Information both already read
            "financing") — not a rare edge case, the majority. `=== 'cash'`
            keeps this table off their page too, consistent with how the
            rest of the page already treats them. */}
        {paymentMethodVal === 'cash' && (
          <table className={styles['payment-table']}>
            <tbody>
              <tr>
                <td>First Payment</td>
                <td>50% at Contract Sign</td>
              </tr>
              <tr>
                <td>Second Payment</td>
                <td>50% at Job Completion</td>
              </tr>
            </tbody>
          </table>
        )}

        {gapMedium}

        <p className={sharedStyles['subheading']}>
          INSTALLATION TERMS AND CONDITIONS
        </p>

        {gapMedium}

        {msElectricTerms.slice(0, -1).map(({ body, title }, i) => (
          <React.Fragment key={i}>
            <p style={sx.node}>
              <span style={sx.bold}>{title}. </span>
              {fillCompany(body)}.
            </p>

            {gapSmall}
          </React.Fragment>
        ))}

        {/* Last clause bundled with the signature block so ReportPages'
            auto-chunker moves both together instead of stranding one alone
            on a near-empty page (same pattern as HomeImprovementAgreement's
            SIGNATORIES block). */}
        <div style={gs.flowRootWidth100}>
          <p style={sx.node}>
            <span style={sx.bold}>
              {msElectricTerms[msElectricTerms.length - 1]!.title}.{' '}
            </span>
            {fillCompany(msElectricTerms[msElectricTerms.length - 1]!.body)}.
          </p>

          {gapMedium}

          <p className={sharedStyles['subheading']}>SIGNATORIES</p>

          {gapSmall}

          <p style={sx.node}>
            This contract shall be effective at signing by The Owner and by The
            Contractor, {CO.name}.
          </p>

          {gapMedium}

          <div className={sharedStyles['signatures']}>
            <div className={sharedStyles['signature-container']}>
              <div>
                <p className={sharedStyles['label-signature']}>Owner 1:</p>
                <span>{customerName}</span>
              </div>

              {gs.rowGap24}

              {home_improvement_sign_owner1 && (
                <>
                  <img
                    alt=""
                    className={sharedStyles['signature-img']}
                    src={home_improvement_sign_owner1}
                    width={200}
                  />
                  <p>
                    {c.formatContractDate(
                      home_improvement_sign_owner1_date,
                      solarCompany,
                      'MM/DD/YYYY - hh:mma',
                    )}
                  </p>
                </>
              )}

              {gs.rowGap24}

              {isPDF || home_improvement_sign_owner1 ? (
                <div />
              ) : (
                <Button
                  data-guided-signature="home_improvement_sign_owner1"
                  onClick={toggleSignOwner1}
                  sx={buttonSignatureStyle}
                  variant="contained"
                >
                  Owner 1 Signature
                </Button>
              )}
            </div>

            {hasOwner2 && (
              <div className={sharedStyles['signature-container']}>
                <div>
                  <p className={sharedStyles['label-signature']}>Owner 2:</p>
                  <span>{owner2NameVal}</span>
                </div>

                {gs.rowGap24}

                {home_improvement_sign_owner2 && (
                  <>
                    <img
                      alt=""
                      className={sharedStyles['signature-img']}
                      src={home_improvement_sign_owner2}
                      width={200}
                    />
                    <p>
                      {c.formatContractDate(
                        home_improvement_sign_owner2_date,
                        solarCompany,
                        'MM/DD/YYYY - hh:mma',
                      )}
                    </p>
                  </>
                )}

                {gs.rowGap24}

                {isPDF || home_improvement_sign_owner2 ? (
                  <div />
                ) : (
                  <Button
                    data-guided-signature="home_improvement_sign_owner2"
                    onClick={toggleSignOwner2}
                    sx={buttonSignatureStyle}
                    variant="contained"
                  >
                    Owner 2 Signature
                  </Button>
                )}
              </div>
            )}

            <div className={sharedStyles['signature-container']}>
              <div>
                <p className={sharedStyles['label-signature']}>
                  Contractor's Representative:
                </p>
                <span>{c.displayHomeRepOnContract(homeRep)}</span>
              </div>

              {home_improvement_sign_company && (
                <>
                  <img
                    alt=""
                    className={sharedStyles['signature-img']}
                    src={home_improvement_sign_company}
                    width={200}
                  />
                  <p>
                    {c.formatContractDate(
                      home_improvement_sign_company_date,
                      solarCompany,
                      'MM/DD/YYYY - hh:mma',
                    )}
                  </p>
                </>
              )}

              {gs.rowGap24}

              <p className={sharedStyles['label-signature']}>{CO.name}</p>

              {gs.rowGap24}

              {isPDF || readOnly || home_improvement_sign_company ? (
                <div />
              ) : (
                <Button
                  onClick={toggleSignCompany}
                  sx={buttonSignatureStyle}
                  variant="contained"
                >
                  Authorized Signature
                </Button>
              )}
            </div>
          </div>
        </div>
      </ReportPages>

      {/* Notice of Cancellation comes BEFORE the Nevada Disclosure Statement
          — matches the physical page order of Fernando's reference PDF
          exactly (opposite of Caballero's HomeImprovementAgreement.tsx,
          which puts Nevada Disclosure first). Literal uncorrected wording
          ("TRACED IN", "TWENTY 20 DAYS") — NOT the B109 corrected text used
          on CE's page; Fernando's model has these as-is and Luis was
          explicit: match it exactly, don't fix typos here. */}
      <ReportPages customerID={customerID} hPad watermark={watermark}>
        {header}

        {gapMedium}

        <p className={sharedStyles['subheading']}>
          NOTICE OF CANCELLATION - THREE DAY RIGHT TO CANCEL
        </p>

        {gapMedium}

        <p style={sx.node}>
          YOU, THE CONSUMER(S), MAY CANCEL THIS TRANSACTION AT ANY TIME PRIOR TO
          MIDNIGHT OF THE THIRD (3) BUSINESS DAY AFTER THE DATE OF THIS
          TRANSACTION.
        </p>

        {gapSmall}

        <p style={sx.node}>
          YOU MAY CANCEL THIS TRANSACTION WITHOUT ANY PENALTY OR OBLIGATION,
          WITHIN THREE (3) CALENDAR DAYS FROM THE ABOVE DATE.
        </p>

        {gapSmall}

        <p style={sx.node}>
          IF YOU CANCEL, ANY PROPERTY TRACED IN, ANY PAYMENT MADE BY YOU UNDER
          THE CONTRACT OR SALE, AND ANY NEGOTIABLE INSTRUMENT EXECUTED BY YOU
          WILL BE RETURNED WITHIN TEN (10) CALENDAR DAYS FOLLOWING RECEIPT BY
          THE SELLER OF YOUR CANCELLATION NOTICE AND ANY SECURITY INTEREST
          ARISING OUT OF THE TRANSACTION WILL BE CANCELLED.
        </p>

        {gapSmall}

        <p style={sx.node}>
          IF YOU CANCEL YOU MUST MAKE AVAILABLE TO THE SELLER AT YOUR RESIDENCE,
          IN SUBSTANTIALLY AS GOOD CONDITION AS WHEN RECEIVED, ANY GOODS
          DELIVERED TO YOU UNDER THIS CONTRACT OR SALE; OR YOU MAY, IF YOU WISH
          COMPLY WITH THE INSTRUCTIONS OF THE SELLER REGARDING THE RETURN
          SHIPMENT OF THE GOODS AT THE SELLER'S EXPENSE AND RISK.
        </p>

        {gapSmall}

        <p style={sx.node}>
          IF YOU DO MAKE THE GOODS AVAILABLE TO THE SELLER AND THE SELLER DOES
          NOT PICK THEM UP WITHIN TWENTY 20 DAYS OF THE DATE OF YOUR NOTICE OF
          CANCELLATION, YOU MAY RETAIN OR DISPOSE OF THE GOODS WITHOUT ANY
          FURTHER OBLIGATION.
        </p>

        {gapSmall}

        <p style={sx.node}>
          IF YOU FAIL TO MAKE THE GOODS AVAILABLE TO THE SELLER, OR IF YOU AGREE
          TO RETURN THE GOODS TO THE SELLER AND FAIL TO DO SO, THEN YOU REMAIN
          LIABLE FOR PERFORMANCE OF ALL OBLIGATIONS UNDER THE CONTRACT.
        </p>

        {gapSmall}

        <p style={sx.node}>
          TO CANCEL THIS TRANSACTION EMAIL A SIGNED AND DATED COPY OF THIS
          CANCELLATION NOTICE.
        </p>

        {gapSmall}

        <div style={gs.flowRootWidth100}>
          <p style={sx.bold}>I HEREBY CANCEL THIS TRANSACTION</p>

          {gapMedium}

          <div className={sharedStyles['signatures']}>
            <div className={sharedStyles['signature-container']}>
              <div>
                <p className={sharedStyles['label-signature']}>Owner 1:</p>
                <span>{customerName}</span>
              </div>

              {gs.rowGap24}

              {home_improvement_cancel_sign_owner1 && (
                <>
                  <img
                    alt=""
                    className={sharedStyles['signature-img']}
                    src={home_improvement_cancel_sign_owner1}
                    width={200}
                  />
                  <p>
                    {c.formatContractDate(
                      home_improvement_cancel_sign_owner1_date,
                      solarCompany,
                      'MM/DD/YYYY - hh:mma',
                    )}
                  </p>
                </>
              )}

              {gs.rowGap24}

              {isPDF || home_improvement_cancel_sign_owner1 ? (
                <div />
              ) : (
                <Button
                  onClick={toggleCancelSignOwner1}
                  sx={buttonSignatureStyle}
                  variant="contained"
                >
                  Owner 1 Signature
                </Button>
              )}
            </div>

            {hasOwner2 && (
              <div className={sharedStyles['signature-container']}>
                <div>
                  <p className={sharedStyles['label-signature']}>Owner 2:</p>
                  <span>{owner2NameVal}</span>
                </div>

                {gs.rowGap24}

                {home_improvement_cancel_sign_owner2 && (
                  <>
                    <img
                      alt=""
                      className={sharedStyles['signature-img']}
                      src={home_improvement_cancel_sign_owner2}
                      width={200}
                    />
                    <p>
                      {c.formatContractDate(
                        home_improvement_cancel_sign_owner2_date,
                        solarCompany,
                        'MM/DD/YYYY - hh:mma',
                      )}
                    </p>
                  </>
                )}

                {gs.rowGap24}

                {isPDF || home_improvement_cancel_sign_owner2 ? (
                  <div />
                ) : (
                  <Button
                    onClick={toggleCancelSignOwner2}
                    sx={buttonSignatureStyle}
                    variant="contained"
                  >
                    Owner 2 Signature
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      </ReportPages>

      <cp.PageReport company={solarCompany} hPad watermark={watermark}>
        <p className={sharedStyles['subheading']}>
          NEVADA DISCLOSURE STATEMENT
        </p>

        {gapSmall}

        <p style={{ ...sx.bold, whiteSpace: 'nowrap' }}>
          RESIDENTIAL CONSTRUCTION RECOVERY FUND
        </p>

        {gapSmall}

        <p style={sx.node}>
          Payment may be available from the Recovery Fund if you are damaged
          financially by a project performed on your residence pursuant to a
          contract, including construction, remodeling, repair or other
          improvements, and the damage resulted from certain specified
          violations of Nevada law by a contractor licensed in this State. To
          obtain information relating to the Recovery Fund and filing a claim
          for recovery from the Recovery Fund, you may contact the Nevada State
          Contractors Board at the following locations:
        </p>

        {gapMedium}

        <p style={sx.bold}>Southern Nevada:</p>
        <p style={sx.node}>Address: 2310 Corporate Circle, Ste 200</p>
        <p style={sx.node}>Henderson, NV 89074</p>
        <p style={sx.node}>Phone: (702) 486-1100</p>
        <p style={sx.node}>Fax: (702) 486-1190</p>

        {gapMedium}

        <p style={sx.bold}>Northern Nevada:</p>
        <p style={sx.node}>Address: 5390 Kietzke Lane, Ste 102</p>
        <p style={sx.node}>Reno, NV 89511</p>
        <p style={sx.node}>Phone: (775) 688-1141</p>
        <p style={sx.node}>Fax: (775) 688-1271</p>
      </cp.PageReport>
    </>
  )
})

const gapBig = gs.rowGap48
const gapMedium = gs.rowGap32
const gapSmall = gs.rowGap16

const sxNode = {
  alignSelf: 'flex-start',
  fontFamily: c.light.fontFamily,
  fontSize: 11,
  margin: 0,
}
const sx = {
  bold: { ...sxNode, fontWeight: 'bold' },
  // Company block on the reverted cover page - same shape FirstElectricContract
  // used for MSE before be3df503, centered because this component draws the
  // logo above the text rather than beside it.
  companyInfoCentered: {
    fontFamily: c.light.fontFamily,
    margin: 0,
    textAlign: 'center' as const,
  },
  // Label for the CUSTOMER INFORMATION block in the header. Bold and
  // centred so it reads as a section of the title area rather than as
  // another line of the company's own details directly above it.
  partyHeading: {
    fontFamily: c.light.fontFamily,
    fontSize: 11,
    fontWeight: 'bold' as const,
    margin: 0,
    textAlign: 'center' as const,
    // Only the ESR written cover uses it now; keeps the PDF capture from
    // wrapping it onto the line below (47a8d976).
    whiteSpace: 'nowrap' as const,
  },
  grandTotal: { ...sxNode, textAlign: 'end' as const, width: '100%' },
  // Scope of Work sign band (ticket 12915736128). Jaron 2026-09-01: two
  // plain buttons, not a signature box. `scopeSignRow` is a relative flex
  // row — `confirmBtnBlock` (the "Confirm Equipment" button) centred via
  // `justify-content: center`, `totalRight` taken out of flow and pinned to
  // the top-right so it can't pull the centred button off-centre. Both
  // sub-blocks are `flex-direction: column` so the placed-initials image and
  // its date stack under whatever they belong to once signed.
  scopeSignRow: {
    alignItems: 'flex-start' as const,
    display: 'flex' as const,
    justifyContent: 'center' as const,
    // `totalRight` is absolute, so its real height never counts toward this
    // row's box — `minHeight` is a guess standing in for it. 72 was sized for
    // the UNSIGNED case only (TOTAL line + gap + button, ~68px). Once BOTH
    // buttons are signed, `totalRight` grows to TOTAL + gap + "Confirm Total"
    // label + placed-initials image (height: auto, so its real size depends
    // on how the customer drew it) + date — comfortably over 72px, so it
    // rode down into FINANCING INFORMATION below (Jaron, 12993405898,
    // Luis Test Customer B78). Bumped to fit that signed case with the
    // initials image at a typical size. An unusually tall/wide signature can
    // still in principle push past this — absolute positioning can't reserve
    // space for content it doesn't measure, only a real layout change
    // (an in-flow spacer sized to match `totalRight`) would close that
    // completely, which is more than this ticket's scope.
    minHeight: 120,
    position: 'relative' as const,
    width: '100%',
  },
  // Centre block: the "Confirm Equipment" button, or (once signed) its label
  // + placed initials + date.
  confirmBtnBlock: {
    alignItems: 'center' as const,
    display: 'flex' as const,
    flexDirection: 'column' as const,
    gap: 4,
    width: 'fit-content' as const,
  },
  // Signing v2: Financing Information signature, under its terms.
  financingSignBlock: {
    alignItems: 'center' as const,
    display: 'flex' as const,
    flexDirection: 'column' as const,
    gap: 4,
    marginTop: 8,
    width: '100%',
  },
  // Kept for the "Confirm Equipment" caption shown above the placed initials
  // in the signed state.
  confirmBoxLabel: {
    fontFamily: c.light.fontFamily,
    fontSize: 11,
    fontWeight: 'bold' as const,
    margin: 0,
  },
  // Right block: TOTAL with the "Confirm Total" button directly beneath it
  // (Jaron: TOTAL back on the right, before the buttons; Confirm Total below
  // the TOTAL). Absolute so it doesn't shift the centred button.
  totalRight: {
    alignItems: 'flex-end' as const,
    display: 'flex' as const,
    flexDirection: 'column' as const,
    gap: 8,
    position: 'absolute' as const,
    right: 0,
    top: 0,
  },
  // `sx.bold` carries `alignSelf: 'flex-start'` from `sxNode`; in this column
  // that fights `align-items: flex-end`, so pin it explicitly.
  totalAmount: {
    ...sxNode,
    alignSelf: 'flex-end' as const,
    fontWeight: 'bold' as const,
  },
  // A stored initials image once one of the buttons has been used.
  placedInitials: {
    display: 'block' as const,
    height: 'auto' as const,
    maxWidth: 140,
    objectFit: 'contain' as const,
  },
  // Label left, price hard right - against the same edge the TOTAL below uses,
  // and the same edge <DetailLine> aligns the Smart Home equipment to.
  //
  // These were label + 20 fixed blank characters + price. That reads fine for
  // the single line this section used to be; with one line per product the
  // prices land wherever each label's length leaves them, so the page ends up
  // with an aligned equipment table above a ragged staircase of money below it.
  // On a contract that gets signed.
  scopeLine: {
    ...sxNode,
    alignSelf: 'stretch' as const,
    display: 'flex' as const,
    justifyContent: 'space-between' as const,
    width: '100%',
  },
  logo: { left: horizontalPad, position: 'absolute' as const, top: '96px' },
  node: sxNode,
  textarea: {
    backgroundColor: 'white',
    color: 'black',
    fontFamily: c.light.fontFamily,
    minHeight: 60,
    width: '100%',
  },
  title: {
    fontFamily: c.light.fontFamily,
    fontSize: 20,
    // Bold, and sitting BELOW the logo rather than beside it.
    // Jaron, Slack 2026-08-21 02:07: "This looks bad. We can't have these
    // things on top of each other / Why is the title not bold?" - the logo
    // is absolutely positioned at top 96px and runs to 116px, and the title
    // naturally landed at 80-110px, so the artwork covered it.
    // First attempt cleared it sideways with a 220px left margin, the way
    // FirstElectricContract does. That stopped the overlap but pushed the
    // title off-centre, which Luis rejected on sight: it belongs centred,
    // under the logo. 52px of top margin puts it at ~132px, clear of the
    // logo's 116px bottom edge, and centred across the full page width.
    fontWeight: 'bold' as const,
    margin: 0,
    marginTop: 52,
    textAlign: 'center' as const,
  },
}

/**
 * Literal, word-for-word text from Fernando's reference PDF (pages 3-5) —
 * NOT the shared `homeImprovementAgreementTerms` (constants.ts), which
 * diverges from this at clause 21 (CE's warranty text was later customized
 * to "2 Years Workmanship / 10 Year Manufacture" — MSE's reference PDF
 * literally says "1 Years Workmanship" / plain "Manufacture Warranty on any
 * Material", no year specified). Kept as its own local array per Luis's
 * "su propia vaina" directive — not exported, only ever used here.
 */
const msElectricTerms: ReadonlyArray<{ body: string; title: string }> = [
  {
    body: 'We will (a) procure and install the HOME IMPROVEMENT unit(s) for and on your property in accordance with standard industry practices, (b) make any necessary arrangements for purchase and delivery of equipment, and (c) complete any related home improvement projects listed and described in "Project Scope Description" on page 1 of this agreement',
    title: '1. DESCRIPTION OF PROJECT',
  },
  {
    body: 'Contractor shall use reasonable efforts to commence the Installation on or prior to the estimated installation date and we will commence the Installation as soon as it is practical once the following conditions have been satisfied: (a) We have confirmed, at our sole discretion, that (i) your ability to pay for the Installation either personally or through third party financing, which may include our receipt of a financing contract between you and that lender and (ii) the suitability of the Property for the construction, installation and operation of the HOME IMPROVEMENT system; and (b) Our receipt of all zoning, land use, and building permits as well as any consents of any other third party, including any mortgagor or lessor, or the electrical utility, as applicable, necessary for the Installation. We will use commercially reasonable efforts to complete the Installation within the time period verbally communicated to the Owner(s) by the Contractor, subject to any Changes requested by the Owner(s) which will be presumed to elongate the completion date',
    title: '2. TIME OF PERFORMANCE',
  },
  {
    body: "If Applicable, The project will be constructed according to the drawings and specifications provided to the Owner(s) by the Contractor, which are incorporated into this Contract by reference, which have been examined by Owner(s). Unless otherwise specifically provided in the drawings or specifications, the Contractor will obtain and pay for all required building permits and Owner(s) will pay assessments and charges required by public bodies and utilities for financing or re-paying the cost of sewers, storm drains, water service, and other utilities including charges for sewer and storm drain reimbursement, revolving funds, hookup, and other similar charges. Unless otherwise specified, work does not include any changes or alterations from the drawings or specifications that may be required by any public body, utility, or inspector. The Contractor, at Contractor's option, may alter specifications only to comply with requirements of governmental agencies having jurisdiction over same. The cost of any alteration undertaken to comply with any such requirements shall be in addition to the Contract price specified herein",
    title: '3. DRAWINGS AND PERMITS',
  },
  {
    body: 'The price for the Installation is the "System Price". The price for the Additional Services if any, is the "Additional Services Price". The Total Contract Price is the sum of the System Price and the Additional Services Price. All prices are as set forth in pricing section above. Subject to the terms of this Contract and any financing contract, if applicable, you will be responsible for payment to us for the Installation',
    title: '4. PRICE AND PAYMENT',
  },
  {
    body: 'Unless otherwise specified in this Contract, the removal, disturbance, or transportation of hazardous materials, including but not limited to asbestos, is the sole responsibility of the Owner under this Contract. The parties agree that, if the Contractor encounters hazardous materials during the Project, the Contractor shall stop work immediately and notify Owner(s) who may then retain a qualified Contractor to perform the work at Owner(s) cost',
    title: '5. HAZARDOUS MATERIALS',
  },
  {
    body: "This Contract is made between Owner(s) and the Contractor. Contractor may not be the person who sold you the HOME IMPROVEMENT System and/or other products and services covered in this Contract. The terms of this Contract represent the agreement between Owner(s) and the Contractor. Owner(s) should not rely on any statements or representations made about the fulfillment or operation of the terms of this Contract from any third-party and should direct inquiry or clarification directly to the Contractor. Any subsequent amendment, modification, or Contract, which alters this Contract, and which is signed or initialed by Contractor and Owner(s), shall be deemed a part of this Contract and shall be controlling in case of conflict with any other provision in Contract. If Owner(s), a lender, or any public body or inspector directs any modification or addition to the work covered by this Contract, the charge for that extra work shall be determined in advance and the cost shall be added to the Contract price in addition to Contractor's usual fee for overhead and profit. Owner shall make payments for all extra work as that work progresses, concurrently with regularly scheduled payments. Contractor shall do no extra work without the prior written authorization of the Owner(s). Any authorization for extra work shall show the agreed terms and shall be approved and signed by both parties",
    title: '6. CHANGE ORDERS, AMENDMENTS AND MODIFICATIONS',
  },
  {
    body: 'Unless specifically included in this Contract, any electrical work to change any existing service panel other than the addition of circuit breakers or fuse blocks to distribute electric current to new outlets is not covered by, and is not made a part of, this Contract. This Contract does not include changes to existing wiring in areas undisturbed by alterations. All existing electrical wiring systems are assumed to be, and Owner(s) represents them to be, adequate to carry the load imposed by existing work. Any work necessary to correct these existing conditions shall be an extra work item subject to point number six (6) of this Contract',
    title: '7. ELECTRICAL SERVICE',
  },
  {
    body: 'If a "Completion Certificate" signature is required by a financial institution, client agrees to sign the required "Completion Certificate" when all equipment per this agreement has been installed on the property. After signing of the "Completion Certificate", the Contractor will proceed to schedule and complete the permit inspection, any modifications to the system if required by municipality\'s building department and submit PTO to utility',
    title: '8. COMPLETION CERTIFICATE',
  },
  {
    body: 'You grant the Contractor and its employees and representatives the right to reasonably access all of the property as necessary for the purpose of installing, constructing, operating, repairing, removing and replacing the system or making any additions to the system',
    title: '9. PROPERTY ACCESS',
  },
  {
    body: 'The Owner(s) is responsible for obtaining insurance coverage for any loss or damage to the system. The Owner(s) should consult an insurance professional to understand how to protect and the risk of loss or damage to the system. The Owner(s) should also consult their home insurer about the potential impact of installing a system',
    title: '10. INSURANCE',
  },
  {
    body: 'Except for items designated by Owner(s) in writing prior to the commencement of construction, Contractor shall dispose of all material removed from structures in the course of alteration. Contractor shall remove all construction debris on termination of work and shall leave the premises in neat, broom-clean condition',
    title: '11. REMOVAL OF MATERIAL AND DEBRIS',
  },
  {
    body: 'Owner(s) agrees to remove from the jobsite or to otherwise protect any personal property including, but not limited to, carpets, rugs, drapes, furniture, shrubs, and plantings. Contractor shall not be held responsible for damage to or loss of any items of personal property',
    title: "12. PROTECTION OF OWNER'S PROPERTY",
  },
  {
    body: "This document and all documents incorporated by reference constitute the parties' entire Contract. No other contracts, oral or written, regarding the work to be performed under this contract exist between the parties. This contract shall be construed in accordance with, and governed by, the laws of the State of Nevada",
    title: '13. INTEGRATION CLAUSE',
  },
  {
    body: "All claims and disputes arising under or relating to this Contract are to be settled by binding arbitration in the State of Nevada or another location mutually agreeable to the parties (however the State of Nevada shall control if the parties cannot agree upon a location). An award of arbitration may be confirmed in a court of competent jurisdiction. The prevailing party shall be entitled to recover from the non-prevailing party all of their costs and attorney's fees related to the claims and disputes raised by either party. NOTICE: BY SIGNING THIS AGREEMENT THE BUYER(S) IS/ARE AGREEING TO HAVE ANY DISPUTE ARISING OUT OF OR RELATED TO THE AGREEMENT DECIDED BY NEUTRAL ARBITRATION AS PROVIDED BY NEVADA LAW AND BUYER(S) IS/ARE GIVING UP ANY RIGHTS BUYER(S) MIGHT POSSESS TO HAVE THE DISPUTE LITIGATED IN A COURT AND DECIDED BY A JURY BY SIGNING THIS AGREEMENT BUYER(S) IS/ARE FURTHER GIVING UP BUYER(S) RIGHTS TO DISCOVERY AND APPEAL APPLICABLE TO COURT PROCEEDINGS, UNLESS THOSE RIGHTS ARE SPECIFICALLY INCLUDED IN THIS SECTION 18. IF BUYER(S) REFUSE/S TO SUBMIT TO ARBITRATION AFTER AGREEING TO THIS PROVISION, BUYER(S) MAY BE COMPELLED TO ARBITRATE UNDER THE AUTHORITY OF APPLICABLE LAW. BUYER(S) AGREE/S THAT BUYER'S AGREEMENT TO THIS SECTION 18 IS VOLUNTARY. BUYER(S) HAS/HAVE READ AND UNDERSTANDS THE FOREGOING AND AGREES TO SUBMIT DISPUTES ARISING OUT OF OR RELATED TO THE AGREEMENT TO NEUTRAL ARBITRATION",
    title: '14. DISPUTES',
  },
  {
    body: 'If minor items of corrective or repair work remain to be accomplished by Contractor after the project is ready for use, Contractor shall perform the work expeditiously and Owner(s) shall not withhold any payment pending completion of that work',
    title: '15. CORRECTIVE OR REPAIR WORK',
  },
  {
    body: 'If Contractor has not received payment from Owner(s) more than 30 days after the date of payment becoming due, Contractor may assess a late charge at the lesser of $50 per week or the highest amount permitted by applicable law on all payments past due',
    title: '16. LATE PAYMENT PENALTY',
  },
  {
    body: 'Customer may cancel this transaction at any time prior to midnight of the third (3) business day after the date of this transaction',
    title: '17. 3 DAY RIGHT TO CANCEL',
  },
  {
    body: 'In the event you cancel this Contract after the 3 day right to cancel has passed, you agree to pay any restocking and freight charges incurred by the contractor of any returned material or equipment',
    title: '18. CANCELLATION FEES',
  },
  {
    body: 'Any notice required or permitted under this Contract may be given by ordinary or electronic mail at the addresses specified in this Contract. If either party changes his or her address, that party shall provide written notice of the change to the other party. Notice shall be considered received one day after it is deposited in the mail with postage prepaid',
    title: '19. NOTICE',
  },
  {
    body: 'NO EMPLOYEE OR REPRESENTATIVE OF {{COMPANY_NAME_UPPER}} IS AUTHORIZED TO MAKE ANY PROMISE TO YOU THAT IS NOT CONTAINED IN WRITTEN DOCUMENT(S) PROVIDED TO OWNER(S) THAT HAVE BEEN EXECUTED BY THE CONTRACTOR CONCERNING COST SAVINGS, TAX BENEFITS, OR GOVERNMENT OR UTILITY INCENTIVES. YOU SHOULD NOT REPLY UPON ANY PROMISE OR ESTIMATE THAT IS NOT INCLUDED IN WRITTEN DOCUMENT(S) PROVIDED TO OWNER THAT HAVE BEEN EXECUTED BY THE CONTRACTOR',
    title: '20. LIMITED AUTHORITY OF INDEPENDENT CONTRACTOR',
  },
  {
    body: 'The warranty requirements for {{COMPANY_NAME}} are as follows: a. 1 Years Workmanship Warranty. b. Manufacture Warranty on any Material',
    title: '21. WARRANTY',
  },
]
